'use client'

import { AuthScreenTitle } from '@repo/ui/AuthScreenTitle'
import { PasswordInput } from '@repo/ui/PasswordInput'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useInitialDataFetchStore } from '@repo/store/useInitialDataFetchStore'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useNavigate, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { useToastStore } from '@repo/store/useToastStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUIInput from '@mui/material/TextField'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import packageJson from '../../../package.json'
import PropTypes from 'prop-types'

const Login = ({ login_DI = null }) => {
  const {
    login,
    getProfile,
    iframeIsLoggedIn,
    isLoggedIn,
    setIframeIsLoggedIn,
    setIsLoggedIn,
    loginPageLoading,
    setLoginPageLoading,
    deepLink,
  } = useAuthenticationStore()
  const { fetchInitialData } = useInitialDataFetchStore()
  const { isLoading } = useLoadingStore()
  const { setIframeSrc } = useUrlStore()
  const { setVersion, isLocal, environmentName, fetchCanCompanyUseMicrosoftEmail } = useGlobalSettingsStore()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [twoFactorAuthCode, setTwoFactorAuthCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const environment = environmentName === 'production' ? 'prod' : environmentName
  const { reset: resetToasts } = useToastStore()
  const navigate = useNavigate()
  const theme = useTheme()
  const [require2FA, setRequire2FA] = useState(false)

  useEffect(() => {
    // ensure loading is reset on page load so it doesn't get stuck in a loading state
    setLoginPageLoading(false)
  }, [setLoginPageLoading])

  useEffect(() => {
    if (isLoggedIn && (isLocal || iframeIsLoggedIn)) {
      if (deepLink) {
        navigate(`/#`)

        setTimeout(() => {
          navigate(deepLink)
        }, 500)
        setLoginPageLoading(false)
      } else {
        setLoginPageLoading(false)
        navigate(`/#`)
      }
    }
  }, [iframeIsLoggedIn, isLoggedIn, isLocal, navigate, setLoginPageLoading, deepLink])

  const handleLogin = async (e, showLoginButtonBusy = true, clear2FACode = false) => {
    e.preventDefault()

    if (isLoading || loginPageLoading) return

    try {
      if (clear2FACode) setTwoFactorAuthCode('')
      setErrorMessage('')
      setLoginPageLoading(showLoginButtonBusy)
      setIframeIsLoggedIn(false)
      resetToasts()

      const func = login_DI || login
      const { authenticationToken, errorMessage } = await func({
        username,
        password,
        twoFactorAuthCode: clear2FACode ? null : twoFactorAuthCode
      })

      if (errorMessage === 'Missing 2FA code') {
        setLoginPageLoading(false)
        setRequire2FA(true)
      } else {
        handleLoginSuccess(authenticationToken)
      }
    } catch (err) {
      console.log(err)
      setIsLoggedIn(false)
      setIframeIsLoggedIn(false)

      if (err?.status === 422) {
        setErrorMessage(
          'The system has detected that your password must be updated. Please check your email for a password reset link.'
        )
      } else {
        setErrorMessage(err?.response?.data?.errorMessage || 'An error occurred. Please try again.')
      }

      setLoginPageLoading(false)
    }
  }

  const handleLoginSuccess = async (authenticationToken) => {
    try {
      // this needs to be called before getProfile so that the companyCanUseMicrosoftEmail value is set
      await fetchCanCompanyUseMicrosoftEmail()
      await getProfile()
    } catch (err) {
      setErrorMessage('Failed to get your user account details. Please contact support@ospreyapproach.com')
      setIsLoggedIn(false)
      setIframeIsLoggedIn(false)
      setLoginPageLoading(false)
      return
    }

    try {
      await fetchInitialData()
      setVersion(packageJson.version)
    } catch (err) {
      setErrorMessage('Failed to get your configuration details. Please contact support@ospreyapproach.com')
      setIsLoggedIn(false)
      setIframeIsLoggedIn(false)
      setLoginPageLoading(false)
      return
    }

    try {
      setIframeSrc(
        `LoginUsingAuthenticationToken.aspx?authenticationToken=${authenticationToken}&environment=${environment === 'prod1' ? 'prod-la' : environment}`
      )
    } catch (err) {
      setErrorMessage('Failed to create your session. Please contact support@ospreyapproach.com')
      setIsLoggedIn(false)
      setIframeIsLoggedIn(false)
      setLoginPageLoading(false)
    }
  }

  const trimAndSet2FACodeValue = (value) => {
    if (value) setTwoFactorAuthCode(value.trim())
    else setTwoFactorAuthCode('')
  }

  return (
    <>
      <AuthScreenTitle title="Login" description={'Welcome back to Osprey Approach'} />

      <form
        onSubmit={handleLogin}
        style={{
          width: '100%',
          position: 'relative',
          cursor: isLoading || loginPageLoading ? 'wait' : 'default'
        }}
        data-testid="loginForm"
      >
        <MUIStack direction="column" alignItems="center" justifyContent="center" gap={4}>
          <MUIInput
            data-testid="username"
            id="username"
            name="username"
            autoComplete="username"
            label="Username or email"
            type="text"
            sx={{ width: '100%' }}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
          />

          <PasswordInput onChange={(e) => setPassword(e.target.value)} label="Password" />

          {require2FA ? (
            <>
              <MUIAlert severity="warning" sx={{ width: '100%' }}>
                Please enter your 2-Factor Authentication code below
              </MUIAlert>
              <MUIInput
                data-testid="2fa"
                id="2fa"
                name="2fa"
                label="2FA code"
                type="text"
                sx={{ width: '100%' }}
                onChange={(e) => trimAndSet2FACodeValue(e.target.value)}
                value={twoFactorAuthCode}
                required={require2FA}
                autoFocus
              />
              <MUITypography
                variant="body1"
                align="right"
                sx={{ width: '100%', color: 'secondary.main', cursor: 'pointer', mt: -4, mb: 4 }}
                onClick={(e) => handleLogin(e, false, true)}
              >
                {`Didn't get the code?`}
              </MUITypography>
            </>
          ) : null}

          <MUIButton
            variant="contained"
            data-testid="loginBtn"
            type="submit"
            color="primary"
            size="small"
            sx={{ height: '56px', width: '100%' }}
            disabled={Boolean(loginPageLoading)}
          >
            {isLoading || loginPageLoading ? 'Logging in...' : 'Log in'}
          </MUIButton>

          <Link
            to="/auth/request-password-reset"
            data-testid="forgotPasswordLink"
            style={{ color: theme?.palette?.secondary?.dark || 'blue' }}
          >
            <MUITypography
              variant="body1"
              align="center"
              sx={{ width: '100%', color: 'secondary.main', cursor: 'pointer', mt: 0, mb: 4 }}
            >
              Forgotten password?
            </MUITypography>
          </Link>

          <MUITypography
            variant="body1"
            align="center"
            data-testid="errorMessage"
            sx={{ width: '100%', color: 'error.main' }}
          >
            {errorMessage}
          </MUITypography>
        </MUIStack>
      </form>
    </>
  )
}

export default Login

Login.propTypes = {
  login_DI: PropTypes.func
}
